import { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/config/firebaseConfig";
import useUsers from "@/hooks/queries/use-users";
import useTopLevelReciprocalMatches from "@/hooks/queries/use-top-level-reciprocal-matches";
import { ChatParticipant, ReviewFilter, SenderFilter } from "@/types/chat";

export const ARCHIVED_STATUSES = [
  "archived",
  "hired",
  "businessDeclined",
  "candidateDeclined",
];

export function useFilteredChatMatches(
  jobAdId: string | undefined,
  searchTerm: string,
  senderFilter: SenderFilter,
  reviewFilter: ReviewFilter
) {
  const [sortedMatches, setSortedMatches] = useState<ChatParticipant[]>([]);
  const { data: users } = useUsers();
  const {
    data: matchedUsers,
    isLoading: matchesLoading,
    error: matchesError,
  } = useTopLevelReciprocalMatches({
    jobAdId: jobAdId as string,
    orderBy: "desc",
  });

  const isLoading = matchesLoading || !users;

  useEffect(() => {
    if (!matchedUsers || !jobAdId) {
      setSortedMatches([]);
      return;
    }

    // setIsLoading(true);

    let unsubscribers: any[] = [];

    try {
      // First, create a base list of all matches
      const baseMatches = matchedUsers
        .map((match) => {
          const user = users?.find((user) => user.uid === match.candidateId);
          return user
            ? {
                ...user,
                ...match,
                hasProfilePic: user.hasProfilePic,
                latestMessage: null,
              }
            : null;
        })
        .filter(Boolean);

      // Sort by match timestamp initially
      const sortedByMatchTime = baseMatches.sort((a: any, b: any) => {
        const timestampA = a?.timestamp?.toDate().getTime() || 0;
        const timestampB = b?.timestamp?.toDate().getTime() || 0;
        return timestampB - timestampA;
      });

      setSortedMatches(sortedByMatchTime as unknown as ChatParticipant[]);

      // Then set up real-time listeners for messages
      unsubscribers = baseMatches.map((match: any) => {
        const messagesRef = collection(db, `Chats/${jobAdId}/Messages`);
        const latestMessageQuery = query(
          messagesRef,
          where("sellerUid", "==", match.candidateId),
          orderBy("timestamp", "desc"),
          limit(1)
        );

        return onSnapshot(latestMessageQuery, (snapshot) => {
          const latestMessage = snapshot.docs[0]?.data();
          setSortedMatches((prev) => {
            const updated = prev.map((user) => {
              if (user.candidateId === match.candidateId) {
                return { ...user, latestMessage } as any;
              }
              return user;
            });

            // Sort by latest message timestamp if available, otherwise fall back to match timestamp
            return updated.sort((a, b) => {
              const timestampA =
                (a as any).latestMessage?.timestamp?.toDate?.().getTime() ||
                (a as any).timestamp?.toDate().getTime() ||
                0;
              const timestampB =
                (b as any).latestMessage?.timestamp?.toDate?.().getTime() ||
                (b as any).timestamp?.toDate().getTime() ||
                0;
              return timestampB - timestampA;
            });
          });
        });
      });

      // setIsLoading(false);
    } catch (error) {
      console.error("Error processing matches:", error);
      setSortedMatches([]);
      // setIsLoading(false);
    }

    return () => {
      unsubscribers.forEach((unsub: any) => unsub());
    };
  }, [matchedUsers, jobAdId, users]);

  // useEffect(() => {
  //   if (!jobAdId || !matchedUsers || !users) {
  //     setSortedMatches([]);
  //     return;
  //   }

  //   const baseMatches = matchedUsers
  //     .map((match) => {
  //       const user = users.find((user) => user.uid === match.candidateId);
  //       return user ? { ...user, ...match, latestMessage: null } : null;
  //     })
  //     .filter((u): u is ChatParticipant => u !== null)
  //     .sort(
  //       (a, b) =>
  //         (b.timestamp?.toDate().getTime() || 0) -
  //         (a.timestamp?.toDate().getTime() || 0)
  //     );

  //   setSortedMatches(baseMatches);

  //   const unsubscribers = baseMatches.map((match) => {
  //     const messagesRef = collection(db, `Chats/${jobAdId}/Messages`);
  //     const q = query(
  //       messagesRef,
  //       where("sellerUid", "==", match.candidateId),
  //       orderBy("timestamp", "desc"),
  //       limit(1)
  //     );

  //     return onSnapshot(q, (snapshot) => {
  //       if (snapshot.empty) return;
  //       const latestMessage = snapshot.docs[0].data();

  //       setSortedMatches((prevMatches) => {
  //         const updatedMatches = prevMatches.map((user) =>
  //           user.candidateId === match.candidateId
  //             ? { ...user, latestMessage }
  //             : user
  //         );

  //         return updatedMatches.sort((a, b) => {
  //           const timeA =
  //             a.latestMessage?.timestamp?.toDate?.().getTime() ||
  //             a.timestamp?.toDate().getTime() ||
  //             0;
  //           const timeB =
  //             b.latestMessage?.timestamp?.toDate?.().getTime() ||
  //             b.timestamp?.toDate().getTime() ||
  //             0;
  //           return timeB - timeA;
  //         });
  //       });
  //     });
  //   });

  //   return () => unsubscribers.forEach((unsub) => unsub());
  // }, [jobAdId, matchedUsers, users]);

  // const filteredMatches = useMemo(() => {
  //   return sortedMatches.filter((match) => {
  //     const name = `${match.givenName || ""} ${match.surname || ""}`
  //       .trim()
  //       .toLowerCase();
  //     if (!name.includes(searchTerm.toLowerCase())) {
  //       return false;
  //     }

  //     switch (senderFilter) {
  //       case "ai":
  //         return match.latestMessage?.sender === "business-automated";
  //       case "business":
  //         return match.latestMessage?.sender === "business";
  //       case "seller":
  //         return match.latestMessage?.sender === "seller";
  //       case "all":
  //       default:
  //         return true;
  //     }
  //   });
  // }, [sortedMatches, searchTerm, senderFilter]);

  const filteredMatches = sortedMatches.filter((match) => {
    const name = `${match.givenName || ""} ${match.surname || ""}`.trim();
    const nameMatches = name.toLowerCase().includes(searchTerm.toLowerCase());

    // Apply sender filter
    let senderMatches = true;
    if (senderFilter === "ai") {
      senderMatches =
        (match as ChatParticipant).latestMessage?.sender ===
        "business-automated";
    } else if (senderFilter === "business") {
      senderMatches =
        (match as ChatParticipant).latestMessage?.sender === "business";
    } else if (senderFilter === "seller") {
      senderMatches =
        (match as ChatParticipant).latestMessage?.sender === "seller";
    }

    // Apply review status filter
    let reviewMatches = true;
    if (reviewFilter !== "all") {
      const reviewStatus = (match as any).reviewStatus;
      if (reviewFilter === "marked_for_review") {
        reviewMatches = reviewStatus === "marked_for_review";
      } else if (reviewFilter === "not_for_review") {
        reviewMatches = reviewStatus === "not_for_review";
      } else if (reviewFilter === "pending") {
        reviewMatches = !reviewStatus || reviewStatus === "pending";
      }
    }

    return nameMatches && senderMatches && reviewMatches;
  });

  const { activeMatches, archivedMatches } = useMemo(() => {
    const active: ChatParticipant[] = [];
    const archived: ChatParticipant[] = [];

    for (const match of filteredMatches) {
      if (ARCHIVED_STATUSES.includes(match.status)) {
        archived.push(match);
      } else {
        active.push(match);
      }
    }
    return { activeMatches: active, archivedMatches: archived };
  }, [filteredMatches]);

  return { activeMatches, archivedMatches, isLoading, error: matchesError };
}
